import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Loader2, Search, Wifi, WifiOff, Truck, ArrowUpDown, ArrowUp, ArrowDown,
  Download, ChevronDown, ChevronRight, Users, Trash2, Pencil, GitMerge, Unlink, Car, Upload, Plus, Layers, Terminal, Check,
  Bike, Ship, Bus, PersonStanding, Tractor, AmbulanceIcon, HardHat, UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useDateLocale } from '@/hooks/useDateLocale';
import * as XLSX from 'xlsx';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useTraccarWebSocket } from '@/hooks/useTraccarWebSocket';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  fetchDevices, fetchPositions, fetchGroups, fetchUsers,
  fetchUsersForDevice, fetchPermissionsMap, fetchDeviceUsersMap, userGroupsFromMap,
  deleteDevice, createDevice, updateDevice,
  createGroup, updateGroup, deleteGroup, linkDevice, unlinkDevice,
  type Device, type Position, type Group, type User,
} from '@/lib/traccar-api';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { fetchCustomers, fetchDeviceMerges, deleteDeviceMerge, type DeviceMerge, type Vehicle } from '@/lib/reseller-api';
import { EditDeviceDialog } from '@/components/devices/EditDeviceDialog';
import { MergeDevicesDialog } from '@/components/devices/MergeDevicesDialog';
import { AddVehicleDialog } from '@/components/devices/AddVehicleDialog';
import { SendCommandDialog } from '@/components/devices/SendCommandDialog';
import { cn } from '@/lib/utils';

type DeviceSortField = 'id' | 'uniqueId' | 'name' | 'makeModel' | 'category' | 'status' | 'lastUpdate' | 'address' | 'group' | 'users' | 'customer' | 'odometer' | 'engineHours' | 'protocol' | 'deviceModel' | 'phone';
type GroupSortField = 'id' | 'name' | 'devicesCount' | 'users';
type SortDirection = 'asc' | 'desc';

const Devices = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useTraccarSession();
  const { isReseller } = useResellerAccess();

  const {
    positions: wsPositions, devices: wsDevices, isConnected: wsConnected,
    connect: wsConnect, disconnect: wsDisconnect,
  } = useTraccarWebSocket();

  const [activeTab, setActiveTab] = useState('devices');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const [sortField, setSortField] = useState<DeviceSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [groupSortField, setGroupSortField] = useState<GroupSortField>('name');
  const [groupSortDirection, setGroupSortDirection] = useState<SortDirection>('asc');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [filterNoCustomer, setFilterNoCustomer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline' | 'moving' | 'idle' | 'parked'>('all');
  const [groupByUsers, setGroupByUsers] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [groupByGroups, setGroupByGroups] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [selectedDevices, setSelectedDevices] = useState<Set<number>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importRawHeaders, setImportRawHeaders] = useState<string[]>([]);
  const [importRawData, setImportRawData] = useState<string[][]>([]);
  const [importColumnMap, setImportColumnMap] = useState<Record<string, string>>({});
  const [importRows, setImportRows] = useState<Record<string, string | number | null>[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDeviceIds, setGroupDeviceIds] = useState<Set<number>>(new Set());
  const [groupDeviceSearch, setGroupDeviceSearch] = useState('');
  const [assignCustomerDevice, setAssignCustomerDevice] = useState<Device | null>(null);
  const [assignCustomerValue, setAssignCustomerValue] = useState<string>('');
  const [isAssigningCustomer, setIsAssigningCustomer] = useState(false);

  // Device-user and group-user mappings
  const [deviceUsersMap, setDeviceUsersMap] = useState<Record<number, User[]>>({});
  const [groupUsersMap, setGroupUsersMap] = useState<Record<number, Group[]>>({});
  const [loadingDeviceIds, setLoadingDeviceIds] = useState<Set<number>>(new Set());

  const enabled = isAuthenticated;

  const { data: initialDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'], queryFn: fetchDevices, enabled
  });
  const { data: initialPositions = [] } = useQuery({
    queryKey: ['positions'], queryFn: fetchPositions, enabled
  });
  const { data: groups = [] } = useQuery({
    queryKey: ['groups'], queryFn: fetchGroups, enabled
  });
  const { data: users = [] } = useQuery({
    queryKey: ['users'], queryFn: fetchUsers, enabled
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'], queryFn: fetchCustomers, enabled: enabled && isReseller
  });
  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'], queryFn: fetchDeviceMerges
  });

  // Merge WS data
  const devices = useMemo(() => {
    if (wsDevices.length === 0) return initialDevices;
    const map = new Map(initialDevices.map(d => [d.id, d]));
    wsDevices.forEach(d => map.set(d.id, d));
    return Array.from(map.values());
  }, [initialDevices, wsDevices]);

  const positions = useMemo(() => {
    return wsPositions.length > 0 ? wsPositions : initialPositions;
  }, [initialPositions, wsPositions]);

  // Fetch user-device and group-user mappings using bulk endpoints (2 requests instead of N)
  const userIdsKey = users.map(u => u.id).join(',');
  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;

    const load = async () => {
      setLoadingDeviceIds(new Set(initialDevices.map(d => d.id)));
      try {
        const perms = await fetchPermissionsMap();
        const deviceUsers = await fetchDeviceUsersMap(users, devices);
        const groupResults = users.map(u => [u.id, userGroupsFromMap(u.id, groups, perms)] as [number, Group[]]);
        if (cancelled) return;
        setDeviceUsersMap(deviceUsers);
        setGroupUsersMap(Object.fromEntries(groupResults));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoadingDeviceIds(new Set());
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsKey]);

  useEffect(() => {
    wsConnect();
    return () => { wsDisconnect(); };
  }, [wsConnect, wsDisconnect]);

  const getPosition = useCallback((deviceId: number): Position | undefined =>
    positions.find(p => p.deviceId === deviceId), [positions]);

  const getGroupName = useCallback((groupId: number): string =>
    groups.find(g => g.id === groupId)?.name || '—', [groups]);

  const getDeviceUsers = useCallback((deviceId: number): User[] =>
    deviceUsersMap[deviceId] || [], [deviceUsersMap]);

  const userCustomerMap = useMemo(() => {
    const map = new Map<number, string>();
    customers.forEach(c => {
      if (c.traccar_user_id) map.set(c.traccar_user_id, c.name);
    });
    return map;
  }, [customers]);

  const deviceById = useMemo(() => {
    const map = new Map<number, (typeof devices)[0]>();
    devices.forEach(d => map.set(d.id, d));
    return map;
  }, [devices]);

  const mergeByPrimary = useMemo(() => {
    const map = new Map<number, DeviceMerge>();
    deviceMerges.forEach(m => map.set(m.primary_device_id, m));
    return map;
  }, [deviceMerges]);

  const mergeBySecondary = useMemo(() => {
    const map = new Map<number, DeviceMerge>();
    deviceMerges.forEach(m => map.set(m.secondary_device_id, m));
    return map;
  }, [deviceMerges]);

  const unmergeMutation = useMutation({
    mutationFn: deleteDeviceMerge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-merges'] });
      toast.success(t('devices.unmergeSuccess'));
    },
    onError: () => {
      toast.error(t('devices.unmergeError'));
    },
  });

  const getDeviceCustomer = useCallback((deviceId: number): string => {
    const du = getDeviceUsers(deviceId);
    for (const u of du) {
      const name = userCustomerMap.get(u.id);
      if (name) return name;
    }
    return '';
  }, [getDeviceUsers, userCustomerMap]);

  const openAssignCustomer = useCallback((device: Device) => {
    const du = getDeviceUsers(device.id);
    const currentCustomerUser = du.find(u => userCustomerMap.has(u.id));
    const currentCustomer = customers.find(c => c.traccar_user_id === currentCustomerUser?.id);
    setAssignCustomerDevice(device);
    setAssignCustomerValue(currentCustomer?.id ?? 'none');
  }, [getDeviceUsers, userCustomerMap, customers]);

  const handleAssignCustomer = async () => {
    if (!assignCustomerDevice) return;
    setIsAssigningCustomer(true);
    try {
      const du = getDeviceUsers(assignCustomerDevice.id);
      const currentCustomerUser = du.find(u => userCustomerMap.has(u.id));
      if (currentCustomerUser) {
        await unlinkDevice(currentCustomerUser.id, assignCustomerDevice.id);
      }
      if (assignCustomerValue && assignCustomerValue !== 'none') {
        const customer = customers.find(c => c.id === assignCustomerValue);
        if (customer?.traccar_user_id) {
          await linkDevice(customer.traccar_user_id, assignCustomerDevice.id);
        }
      }
      // Refresh device users
      const updated = await fetchUsersForDevice(assignCustomerDevice.id).catch(() => [] as User[]);
      setDeviceUsersMap(prev => ({ ...prev, [assignCustomerDevice.id]: updated }));
      toast.success(t('devices.customerAssigned'));
      setAssignCustomerDevice(null);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setIsAssigningCustomer(false);
    }
  };

  // Sort helpers
  const handleDeviceSort = (field: DeviceSortField) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };
  const handleGroupSort = (field: GroupSortField) => {
    if (groupSortField === field) setGroupSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setGroupSortField(field); setGroupSortDirection('asc'); }
  };

  const DeviceSortIcon = ({ field }: { field: DeviceSortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
      : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  };
  const GroupSortIcon = ({ field }: { field: GroupSortField }) => {
    if (groupSortField !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
    return groupSortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
      : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  };

  // Filtered + sorted devices
  const sortedDevices = useMemo(() => {
    const filtered = devices.filter(device => {
      const q = searchQuery.toLowerCase();
      const pos = getPosition(device.id);
      const matchesSearch = !q ||
        device.name.toLowerCase().includes(q) ||
        device.uniqueId.toLowerCase().includes(q) ||
        String(device.id).includes(q) ||
        (pos?.address?.toLowerCase().includes(q)) ||
        getDeviceCustomer(device.id).toLowerCase().includes(q) ||
        getDeviceUsers(device.id).some(u => u.name.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)) ||
        device.phone?.includes(q) ||
        pos?.protocol?.toLowerCase().includes(q);

      const matchesGroup = filterGroup === 'all' || device.groupId === Number(filterGroup);
      const matchesUser = filterUser === 'all' ||
        getDeviceUsers(device.id).some(u => u.id === Number(filterUser));
      const isOnline = device.status === 'online';
      const isMoving = isOnline && pos && pos.speed > 0;
      const isIdle = isOnline && !isMoving && !!pos?.attributes?.ignition;
      const isParked = isOnline && !isMoving && !isIdle;
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'online' && isOnline) ||
        (filterStatus === 'offline' && !isOnline) ||
        (filterStatus === 'moving' && !!isMoving) ||
        (filterStatus === 'idle' && isIdle) ||
        (filterStatus === 'parked' && isParked);

      const matchesNoCustomer = !filterNoCustomer || !getDeviceCustomer(device.id);

      return matchesSearch && matchesGroup && matchesUser && matchesStatus && matchesNoCustomer;
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'id': return (a.id - b.id) * dir;
        case 'name': return a.name.localeCompare(b.name) * dir;
        case 'makeModel': {
          const ma = `${a.attributes?.make || ''} ${a.attributes?.vehicleModel || ''}`.trim();
          const mb = `${b.attributes?.make || ''} ${b.attributes?.vehicleModel || ''}`.trim();
          return ma.localeCompare(mb) * dir;
        }
        case 'category': return (a.category || '').localeCompare(b.category || '') * dir;
        case 'status': {
          const order = { online: 0, unknown: 1, offline: 2 };
          const statusDiff = ((order[a.status] ?? 1) - (order[b.status] ?? 1)) * dir;
          if (statusDiff !== 0) return statusDiff;
          const ta = a.lastUpdate ? new Date(a.lastUpdate).getTime() : 0;
          const tb = b.lastUpdate ? new Date(b.lastUpdate).getTime() : 0;
          return (tb - ta) * dir;
        }
        case 'lastUpdate': {
          const ta = a.lastUpdate ? new Date(a.lastUpdate).getTime() : 0;
          const tb = b.lastUpdate ? new Date(b.lastUpdate).getTime() : 0;
          return (ta - tb) * dir;
        }
        case 'address': {
          const aa = getPosition(a.id)?.address || '';
          const ab = getPosition(b.id)?.address || '';
          return aa.localeCompare(ab) * dir;
        }
        case 'group': return getGroupName(a.groupId).localeCompare(getGroupName(b.groupId)) * dir;
        case 'users': {
          const ua = getDeviceUsers(a.id).map(u => u.name).join(', ');
          const ub = getDeviceUsers(b.id).map(u => u.name).join(', ');
          return ua.localeCompare(ub) * dir;
        }
        case 'customer': return getDeviceCustomer(a.id).localeCompare(getDeviceCustomer(b.id)) * dir;
        case 'protocol': {
          const pa = getPosition(a.id)?.protocol || '';
          const pb = getPosition(b.id)?.protocol || '';
          return pa.localeCompare(pb) * dir;
        }
        case 'deviceModel': {
          const da = (a.model as string) || '';
          const db = (b.model as string) || '';
          return da.localeCompare(db) * dir;
        }
        case 'phone': {
          const pa = get(a.phone) ?? '';
          const pb = get(b.phone) ?? '';
          return pa.localeCompare(pb) * dir;
        }
        default: return 0;
      }
    });
  }, [devices, positions, searchQuery, sortField, sortDirection, filterGroup, filterUser, filterStatus, getPosition, getGroupName, getDeviceUsers, getDeviceCustomer]);

  // For customers: hide secondary devices; for resellers: show all
  const visibleDevices = useMemo(() => {
    if (isReseller) return sortedDevices;
    return sortedDevices.filter(d => !mergeBySecondary.has(d.id));
  }, [sortedDevices, isReseller, mergeBySecondary]);

  const totalPages = Math.max(1, Math.ceil(visibleDevices.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedDevices = visibleDevices.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterGroup, filterUser, filterStatus, filterNoCustomer, sortField, sortDirection]);

  // Grouped by group
  const devicesByGroup = useMemo(() => {
    if (!groupByGroups) return null;
    const map = new Map<number, { group: Group | null; devices: Device[] }>();
    visibleDevices.forEach(d => {
      const key = d.groupId ?? -1;
      if (!map.has(key)) {
        const group = key === -1 ? null : groups.find(g => g.id === key) ?? null;
        map.set(key, { group, devices: [] });
      }
      map.get(key)!.devices.push(d);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (!a.group) return 1;
      if (!b.group) return -1;
      return a.group.name.localeCompare(b.group.name);
    });
  }, [visibleDevices, groupByGroups, groups]);

  // Grouped by user
  const devicesByUser = useMemo(() => {
    if (!groupByUsers) return null;
    const map = new Map<number, { user: User; devices: Device[] }>();
    visibleDevices.forEach(d => {
      const du = getDeviceUsers(d.id);
      if (du.length === 0) {
        const key = -1;
        if (!map.has(key)) map.set(key, { user: { id: -1, name: 'Unassigned', email: '', administrator: false, deviceLimit: 0, userLimit: 0 }, devices: [] });
        map.get(key)!.devices.push(d);
      } else {
        du.forEach(u => {
          if (!map.has(u.id)) map.set(u.id, { user: u, devices: [] });
          map.get(u.id)!.devices.push(d);
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.user.name.localeCompare(b.user.name));
  }, [visibleDevices, groupByUsers, getDeviceUsers]);

  // Groups tab data
  const sortedGroups = useMemo(() => {
    const groupData = groups.map(g => {
      const devCount = devices.filter(d => d.groupId === g.id).length;
      const groupUsers = users.filter(u => (groupUsersMap[u.id] || []).some(ug => ug.id === g.id));
      return { ...g, devicesCount: devCount, users: groupUsers };
    });

    const dir = groupSortDirection === 'asc' ? 1 : -1;
    return groupData.sort((a, b) => {
      switch (groupSortField) {
        case 'id': return (a.id - b.id) * dir;
        case 'name': return a.name.localeCompare(b.name) * dir;
        case 'devicesCount': return (a.devicesCount - b.devicesCount) * dir;
        case 'users': return a.users.map(u => u.name).join(', ').localeCompare(b.users.map(u => u.name).join(', ')) * dir;
        default: return 0;
      }
    });
  }, [groups, devices, users, groupUsersMap, groupSortField, groupSortDirection]);

  const toggleUserExpand = (userId: number) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const expandAll = () => {
    if (devicesByUser) setExpandedUsers(new Set(devicesByUser.map(g => g.user.id)));
  };
  const collapseAll = () => setExpandedUsers(new Set());

  const toggleGroupExpand = (groupId: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };
  const expandAllGroups = () => {
    if (devicesByGroup) setExpandedGroups(new Set(devicesByGroup.map(g => g.group?.id ?? -1)));
  };
  const collapseAllGroups = () => setExpandedGroups(new Set());

  const toggleDeviceSelect = (deviceId: number) => {
    setSelectedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId); else next.add(deviceId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDevices.size === visibleDevices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(visibleDevices.map(d => d.id)));
    }
  };

  const isAllSelected = visibleDevices.length > 0 && selectedDevices.size === visibleDevices.length;
  const isSomeSelected = selectedDevices.size > 0 && selectedDevices.size < visibleDevices.length;
  const selectedDeviceObjects = useMemo(
    () => devices.filter(d => selectedDevices.has(d.id)),
    [devices, selectedDevices],
  );

  const deleteMutation = useMutation({
    mutationFn: async (deviceIds: number[]) => {
      await Promise.all(deviceIds.map(id => deleteDevice(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setSelectedDevices(new Set());
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['groups'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const [isSavingGroup, setIsSavingGroup] = useState(false);

  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDeviceIds(new Set());
    setGroupDeviceSearch('');
    setGroupDialogOpen(true);
  };

  const openEditGroup = (g: Group) => {
    setEditingGroup(g);
    setGroupName(g.name);
    setGroupDeviceIds(new Set(devices.filter(d => d.groupId === g.id).map(d => d.id)));
    setGroupDeviceSearch('');
    setGroupDialogOpen(true);
  };

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    setIsSavingGroup(true);
    try {
      let groupId: number;
      if (editingGroup) {
        const updated = await updateGroup({ ...editingGroup, name: groupName.trim() });
        groupId = updated.id;
      } else {
        const created = await createGroup({ name: groupName.trim() });
        groupId = created.id;
      }

      // Update devices: assign selected ones, unassign previously-in-group ones that were deselected
      const prevInGroup = devices.filter(d => d.groupId === groupId);
      const toAssign = devices.filter(d => groupDeviceIds.has(d.id) && d.groupId !== groupId);
      const toUnassign = prevInGroup.filter(d => !groupDeviceIds.has(d.id));

      await Promise.all([
        ...toAssign.map(d => updateDevice({ ...d, groupId })),
        ...toUnassign.map(d => updateDevice({ ...d, groupId: 0 })),
      ]);

      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setGroupDialogOpen(false);
      setEditingGroup(null);
      setGroupName('');
      setGroupDeviceIds(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setIsSavingGroup(false);
    }
  };

  // Columns that can be mapped from a CSV when importing vehicles (= Traccar devices)
  const IMPORT_COLUMNS = [
    'name', 'uniqueId', 'category', 'groupId',
    'make', 'model', 'year', 'modelYear', 'color', 'licensePlate', 'vin', 'renavan', 'notes',
  ].sort() as const;
  type ImportColumn = typeof IMPORT_COLUMNS[number];

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      let raw = (ev.target?.result as string) || '';
      raw = raw.replace(/^\uFEFF/, '');
      const lines = raw.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) { toast.error(t('common.error')); return; }
      const delim = raw.includes('\t') ? '\t' : raw.includes(';') ? ';' : ',';
      const headers = lines[0].split(delim).map(h => h.trim());
      const data = lines.slice(1).map(line => line.split(delim).map(v => v.trim()));
      // Auto-detect mapping by case-insensitive header match
      const autoMap: Record<string, string> = {};
      headers.forEach(h => {
        const match = IMPORT_COLUMNS.find(col => col.toLowerCase() === h.toLowerCase());
        autoMap[h] = match ?? '';
      });
      setImportRawHeaders(headers);
      setImportRawData(data);
      setImportColumnMap(autoMap);
      setImportRows([]);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const buildImportRows = (map: Record<string, string>): Record<string, string | number | null>[] => {
    return importRawData.map(vals => {
      const row: Record<string, string | number | null> = {};
      importRawHeaders.forEach((h, i) => {
        const field = map[h] as ImportColumn | '';
        if (!field) return;
        const val = vals[i] ?? '';
        if (field === 'groupId') {
          row[field] = val ? parseInt(val) : null;
        } else {
          row[field] = val || null;
        }
        if (row.licensePlate && !row.name) {
          row.name = row.licensePlate
        }
      });
      return row;
    }).filter(r => r.name && r.uniqueId);
  };

  const handleImport = async () => {
    setIsImporting(true);
    let success = 0, failed = 0;
    for (const row of importRows) {
      try {
        const attributes: Record<string, string | number | boolean> = {};
        const attrFields: ImportColumn[] = ['make', 'model', 'year', 'modelYear', 'color', 'licensePlate', 'vin', 'renavan', 'notes'];
        attrFields.forEach(f => { if (row[f] != null) attributes[f === 'model' ? 'vehicleModel' : f] = row[f] as string; });
        await createDevice({
          name: row.name as string,
          uniqueId: row.uniqueId as string,
          ...(row.category ? { category: row.category as string } : {}),
          ...(row.groupId ? { groupId: row.groupId as number } : {}),
          ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
        });
        success++;
      } catch (e) {
        console.error(e)
        failed++; }
    }
    setIsImporting(false);
    queryClient.invalidateQueries({ queryKey: ['devices'] });
    setImportRows([]);
    setImportRawHeaders([]);
    setImportRawData([]);
    setImportColumnMap({});
    setIsImportOpen(false);
    toast.success(t('reseller.vehicle.importDone', { success, failed }));
  };

  const handleBulkDelete = () => {
    if (selectedDevices.size === 0) return;
    if (!confirm(t('devices.deleteConfirm', { count: selectedDevices.size }))) return;
    deleteMutation.mutate(Array.from(selectedDevices));
  };

  const handleExport = useCallback(() => {
    const rows = visibleDevices.map(d => {
      const pos = getPosition(d.id);
      const du = getDeviceUsers(d.id);
      const attrs = pos?.attributes ?? {};
      const speedKmh = pos ? Math.round(pos.speed * 1.852) : '';
      const odometerM = attrs.odometer != null ? Number(attrs.odometer) : attrs.totalDistance != null ? Number(attrs.totalDistance) : null;
      const odometerKm = odometerM != null ? Math.round(odometerM / 1000) : '';
      return {
        ID: d.id,
        Name: d.name,
        'Unique ID': d.uniqueId,
        Status: d.status,
        'Last Update': d.lastUpdate ? format(new Date(d.lastUpdate), 'PPpp', { locale: dateFnsLocale }) : '',
        Address: pos?.address || '',
        Group: getGroupName(d.groupId),
        'Speed (km/h)': speedKmh,
        'Odometer (km)': odometerKm,
        Protocol: pos?.protocol || '',
        Lat: pos?.latitude ?? '',
        Lng: pos?.longitude ?? '',
        Users: du.map(u => u.name).join(', '),
        ...(isReseller ? { Customer: getDeviceCustomer(d.id) } : {}),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Devices');

    const groupRows = sortedGroups.map(g => ({
      ID: g.id,
      Name: g.name,
      Devices: g.devicesCount,
      Users: g.users.map(u => u.name).join(', '),
    }));
    const gs = XLSX.utils.json_to_sheet(groupRows);
    XLSX.utils.book_append_sheet(wb, gs, 'Groups');

    XLSX.writeFile(wb, `devices_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }, [visibleDevices, sortedGroups, getPosition, getGroupName, getDeviceUsers, getDeviceCustomer, isReseller, dateFnsLocale]);

  const isLoading = devicesLoading && initialDevices.length === 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('devices.loading')}</p>
        </div>
      </div>
    );
  }

  const onlineCount = devices.filter(d => d.status === 'online').length;
  const noCustomerCount = isReseller ? devices.filter(d => !getDeviceCustomer(d.id)).length : 0;
  const offlineCount = devices.filter(d => d.status !== 'online').length;
  const movingCount = devices.filter(d => { const p = getPosition(d.id); return d.status === 'online' && p && p.speed > 0; }).length;
  const idleCount = devices.filter(d => { const p = getPosition(d.id); return d.status === 'online' && !(p && p.speed > 0) && !!p?.attributes?.ignition; }).length;
  const parkedCount = devices.filter(d => { const p = getPosition(d.id); return d.status === 'online' && !(p && p.speed > 0) && !p?.attributes?.ignition; }).length;

  const headerContent = (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('devices.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => {
            const v = e.target.value;
            setSearchQuery(v);
            setSearchParams(v ? { search: v } : {}, { replace: true });
          }}
          className="pl-9 w-64"
        />
      </div>
      <Badge variant={wsConnected ? "default" : "secondary"} className="gap-1.5">
        <span className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
        {wsConnected ? t('devices.live') : t('devices.offline')}
      </Badge>
    </div>
  );

  const renderDeviceRow = (device: Device) => {
    const pos = getPosition(device.id);
    const isOnline = device.status === 'online';
    const lastUpdate = device.lastUpdate ? new Date(device.lastUpdate) : null;
    const fixTime = pos?.fixTime ? new Date(pos.fixTime) : null;
    const du = getDeviceUsers(device.id);

    return (
      <TableRow
        key={device.id}
        className={`${selectedDevices.has(device.id) ? 'bg-muted/30' : ''}`}
      >
        <TableCell onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={selectedDevices.has(device.id)}
            onCheckedChange={() => toggleDeviceSelect(device.id)}
          />
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-0">
            <EditDeviceDialog
              device={device}
              groups={groups}
              trigger={
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              }
            />
            {isReseller && (
              <SendCommandDialog
                devices={[device]}
                trigger={
                  <Button variant="ghost" size="icon" className="h-6 w-6" title={t('devices.sendCommand')}>
                    <Terminal className="h-3.5 w-3.5" />
                  </Button>
                }
              />
            )}
            {isReseller && (mergeByPrimary.has(device.id) || mergeBySecondary.has(device.id)) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-orange-500 hover:text-orange-600"
                title={t('devices.unmerge')}
                onClick={e => {
                  e.stopPropagation();
                  if (!confirm(t('devices.unmergeConfirm'))) return;
                  const merge = mergeByPrimary.get(device.id) || mergeBySecondary.get(device.id);
                  if (merge) unmergeMutation.mutate(merge.id);
                }}
                disabled={unmergeMutation.isPending}
              >
                <Unlink className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground font-mono text-sm">{device.id}</TableCell>
        <TableCell className="font-medium max-w-[160px]">
          <div className="flex flex-wrap items-center gap-1">
            {(() => {
              const merge = mergeByPrimary.get(device.id);
              return merge?.display_name && !isReseller ? merge.display_name : device.name;
            })()}
            {isReseller && mergeByPrimary.has(device.id) && (
              <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                <GitMerge className="h-4 w-4 mr-1" />
                {t('devices.mergedSecondary')} → {deviceById.get(mergeByPrimary.get(device.id)!.secondary_device_id)?.name ?? mergeByPrimary.get(device.id)!.secondary_device_id}
              </Badge>
            )}
            {isReseller && mergeBySecondary.has(device.id) && (
              <Badge variant="secondary" className="text-xs">
                {t('devices.mergedPrimary')} → {deviceById.get(mergeBySecondary.get(device.id)!.primary_device_id)?.name ?? mergeBySecondary.get(device.id)!.primary_device_id}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-sm">
          {(() => {
            const icons: Record<string, React.ReactNode> = {
              truck: <Truck className="h-4 w-4" />,
              car: <Car className="h-4 w-4" />,
              motorcycle: <Bike className="h-4 w-4" />,
              bicycle: <Bike className="h-4 w-4" />,
              boat: <Ship className="h-4 w-4" />,
              ship: <Ship className="h-4 w-4" />,
              bus: <Bus className="h-4 w-4" />,
              minibus: <Bus className="h-4 w-4" />,
              person: <PersonStanding className="h-4 w-4" />,
              tractor: <Tractor className="h-4 w-4" />,
              ambulance: <AmbulanceIcon className="h-4 w-4" />,
              construction: <HardHat className="h-4 w-4" />,
            };
            const makeModel = `${device.attributes?.make || ''} ${device.attributes?.vehicleModel || ''}`.trim();
            return (
              <div className="flex flex-col gap-0.5">
                {device.category && (
                  <span title={t(`devices.category_${device.category}`, { defaultValue: device.category })}>
                    {icons[device.category] ?? <span className="text-xs">{device.category}</span>}
                  </span>
                )}
                {makeModel
                  ? <span className="text-xs text-muted-foreground">{makeModel}</span>
                  : (!device.category && <span className="text-muted-foreground">—</span>)}
              </div>
            );
          })()}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-0.5">
            {isOnline ? <Wifi className="h-4 w-4 text-success" /> : <WifiOff className="h-4 w-4 text-muted-foreground" />}
            {lastUpdate ? (
              <span className="text-xs text-muted-foreground" title={format(lastUpdate, 'PPpp', { locale: dateFnsLocale })}>
                {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: dateFnsLocale })}
              </span>
            ) : <span className="text-xs text-muted-foreground">{t('devices.never')}</span>}
            {fixTime && (
              <span className="text-xs text-muted-foreground/70" title={format(fixTime, 'PPpp', { locale: dateFnsLocale })}>
                GPS: {formatDistanceToNow(fixTime, { addSuffix: true, locale: dateFnsLocale })}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          {pos?.address ? (
            <span
              className="text-sm max-w-[200px] block cursor-pointer hover:underline line-clamp-2"
              title={pos.address}
              onClick={() => navigate(`/?deviceId=${device.id}`)}
            >{pos.address}</span>
          ) : <span className="text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="text-sm tabular-nums">
          <div className="flex flex-col">
            {(pos?.attributes?.odometer != null || pos?.attributes?.totalDistance != null)
              ? <span>{(((pos!.attributes.odometer ?? pos!.attributes.totalDistance) as number) / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} km</span>
              : <span className="text-muted-foreground">—</span>}
            {pos?.attributes?.hours != null
              ? <span className="text-muted-foreground text-xs">{((pos.attributes.hours as number) / 3600000).toLocaleString(undefined, { maximumFractionDigits: 1 })} h</span>
              : null}
          </div>
        </TableCell>
        {!isReseller && (
          <TableCell className="text-sm">
            {pos?.protocol || <span className="text-muted-foreground">—</span>}
          </TableCell>
        )}
        {isReseller && (
          <TableCell className="text-muted-foreground font-mono text-xs max-w-[140px]" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col gap-0.5">
              {device.uniqueId.startsWith('not_installed_') ? '' : (
                <button className="hover:underline truncate block max-w-full text-left" title={device.uniqueId} onClick={() => navigate(`/device-stock?imei=${encodeURIComponent(device.uniqueId)}`)}>
                  {device.uniqueId}
                </button>
              )}
              {device.phone && <span className="block text-[10px] text-muted-foreground/70 truncate" title={device.phone}>{device.phone}</span>}
              {(device.model as string) && <span className="block text-[10px] text-muted-foreground/70 truncate font-sans" title={device.model as string}>{device.model as string}</span>}
            </div>
          </TableCell>
        )}
        {isReseller && <TableCell>{getGroupName(device.groupId)}</TableCell>}
        <TableCell>
          {loadingDeviceIds.has(device.id)
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            : du.length > 0
              ? <span className="text-sm" title={du.map(u => u.name).join(', ')}>{du.slice(0, 2).map(u => u.name).join(', ')}{du.length > 2 && ` +${du.length - 2}`}</span>
              : <span className="text-muted-foreground">—</span>}
        </TableCell>
        {isReseller && (
          <TableCell onClick={e => e.stopPropagation()}>
            {loadingDeviceIds.has(device.id)
              ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              : (
                <button
                  className="text-sm hover:underline text-left"
                  onClick={() => openAssignCustomer(device)}
                >
                  {getDeviceCustomer(device.id) || (
                    <span className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                      <Plus className="h-3.5 w-3.5" />
                      {t('devices.assignCustomer')}
                    </span>
                  )}
                </button>
              )}
          </TableCell>
        )}
      </TableRow>
    );
  };

  const deviceColumns: DeviceSortField[] = [
    'id', 'name', 'makeModel', 'status', 'address', 'odometer',
    ...(isReseller ? ['uniqueId' as DeviceSortField] : ['protocol' as DeviceSortField]),
    ...(isReseller ? ['group' as DeviceSortField] : []),
    'users',
    ...(isReseller ? ['customer' as DeviceSortField] : []),
  ];
  const colCount = deviceColumns.length + 2; // +1 checkbox, +1 actions (odometer+engineHours merged)

  const deviceTableHeaders = (
    <TableRow>
      <TableHead className="w-10">
        <Checkbox
          checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
          onCheckedChange={toggleSelectAll}
        />
      </TableHead>
      <TableHead className="w-10">{t('devices.actions')}</TableHead>
      {deviceColumns.map(field => (
        <TableHead key={field} className="cursor-pointer hover:bg-muted/50" onClick={() => handleDeviceSort(field)}>
          <div className="flex items-center gap-1">
            {t(`devices.${field}`)}
            <DeviceSortIcon field={field} />
          </div>
        </TableHead>
      ))}
    </TableRow>
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6 space-y-6">
        {/* Stats + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0" title={`${t('devices.total')}: ${devices.length}`}>
              <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">{t('devices.total')}:</span>
              <span className="font-medium">{devices.length}</span>
            </div>
            <button
              className={`flex items-center gap-2 rounded px-1 hover:bg-muted/50 transition-colors min-w-0 ${filterStatus === 'moving' ? 'bg-muted' : ''}`}
              onClick={() => setFilterStatus(f => f === 'moving' ? 'all' : 'moving')}
              title={`${t('map.moving')}: ${movingCount}`}
            >
              <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
              <span className="text-muted-foreground truncate">{t('map.moving')}:</span>
              <span className="font-medium text-green-600">{movingCount}</span>
            </button>
            <button
              className={`flex items-center gap-2 rounded px-1 hover:bg-muted/50 transition-colors min-w-0 ${filterStatus === 'idle' ? 'bg-muted' : ''}`}
              onClick={() => setFilterStatus(f => f === 'idle' ? 'all' : 'idle')}
              title={`${t('map.idle')}: ${idleCount}`}
            >
              <span className="w-2 h-2 rounded-full bg-yellow-500 shrink-0" />
              <span className="text-muted-foreground truncate">{t('map.idle')}:</span>
              <span className="font-medium text-yellow-600">{idleCount}</span>
            </button>
            <button
              className={`flex items-center gap-2 rounded px-1 hover:bg-muted/50 transition-colors min-w-0 ${filterStatus === 'parked' ? 'bg-muted' : ''}`}
              onClick={() => setFilterStatus(f => f === 'parked' ? 'all' : 'parked')}
              title={`${t('map.parked')}: ${parkedCount}`}
            >
              <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
              <span className="text-muted-foreground truncate">{t('map.parked')}:</span>
              <span className="font-medium text-orange-600">{parkedCount}</span>
            </button>
            <button
              className={`flex items-center gap-2 rounded px-1 hover:bg-muted/50 transition-colors min-w-0 ${filterStatus === 'offline' ? 'bg-muted' : ''}`}
              onClick={() => setFilterStatus(f => f === 'offline' ? 'all' : 'offline')}
              title={`${t('devices.offline')}: ${offlineCount}`}
            >
              <WifiOff className="h-4 w-4 text-destructive shrink-0" />
              <span className="text-muted-foreground truncate">{t('devices.offline')}:</span>
              <span className="font-medium text-destructive">{offlineCount}</span>
            </button>
            {isReseller && noCustomerCount > 0 && (
              <button
                className={`flex items-center gap-2 rounded px-1 hover:bg-muted/50 transition-colors min-w-0 ${filterNoCustomer ? 'bg-muted' : ''}`}
                onClick={() => setFilterNoCustomer(f => !f)}
                title={`${t('devices.noCustomer')}: ${noCustomerCount}`}
              >
                <UserX className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-muted-foreground truncate">{t('devices.noCustomer')}:</span>
                <span className="font-medium text-amber-600">{noCustomerCount}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedDevices.size > 0 && (
              <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-lg border">
                <span className="text-sm font-medium">
                  {t('devices.selected', { count: selectedDevices.size })}
                </span>
                {isReseller && selectedDevices.size === 2 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMergeDialogOpen(true)}
                    className="gap-1"
                  >
                    <GitMerge className="h-4 w-4" />
                    {t('devices.merge')}
                  </Button>
                )}
                {isReseller && (
                  <SendCommandDialog
                    devices={selectedDeviceObjects}
                    trigger={
                      <Button variant="outline" size="sm" className="gap-1">
                        <Terminal className="h-4 w-4" />
                        {t('devices.sendCommand')}
                      </Button>
                    }
                  />
                )}
                {isReseller && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={deleteMutation.isPending}
                    className="gap-1"
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {t('common.delete')}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setSelectedDevices(new Set())}>
                  {t('devices.clearSelection')}
                </Button>
              </div>
            )}
            {isReseller && (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsImportOpen(true)}>
                  <Upload className="h-4 w-4 mr-1" /> {t('reseller.vehicle.import')}
                </Button>
                <Button size="sm" onClick={() => setAddVehicleOpen(true)}>
                  <Car className="h-4 w-4 mr-1" /> {t('reseller.vehicle.add')}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" /> {t('devices.exportExcel')}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="devices">{t('devices.tabs.devices')}</TabsTrigger>
            {isReseller && <TabsTrigger value="groups">{t('devices.tabs.groups')}</TabsTrigger>}
          </TabsList>

          <TabsContent value="devices" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              {isReseller && (
                <Select value={filterGroup} onValueChange={setFilterGroup}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('devices.filterByGroup')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('devices.allGroups')}</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}

              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('devices.filterByUser')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('devices.allUsers')}</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>

              <Button
                variant={groupByUsers ? "default" : "outline"}
                size="sm"
                onClick={() => { setGroupByUsers(!groupByUsers); setGroupByGroups(false); }}
              >
                <Users className="h-4 w-4 mr-1" /> {t('devices.groupByUsers')}
              </Button>

              {isReseller && (
                <Button
                  variant={groupByGroups ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setGroupByGroups(!groupByGroups); setGroupByUsers(false); }}
                >
                  <Layers className="h-4 w-4 mr-1" /> {t('devices.groupByGroups')}
                </Button>
              )}

              {groupByUsers && (
                <>
                  <Button variant="ghost" size="sm" onClick={expandAll}>{t('devices.expandAll')}</Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll}>{t('devices.collapseAll')}</Button>
                </>
              )}

              {groupByGroups && (
                <>
                  <Button variant="ghost" size="sm" onClick={expandAllGroups}>{t('devices.expandAll')}</Button>
                  <Button variant="ghost" size="sm" onClick={collapseAllGroups}>{t('devices.collapseAll')}</Button>
                </>
              )}
            </div>

            {/* Table */}
            <div className="rounded-lg border bg-card">
              {groupByGroups && devicesByGroup ? (
                <Table>
                  <TableHeader>{deviceTableHeaders}</TableHeader>
                  <TableBody>
                    {devicesByGroup.map(({ group, devices: groupDevices }) => {
                      const key = group?.id ?? -1;
                      return (
                        <>
                          <TableRow
                            key={`group-${key}`}
                            className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleGroupExpand(key)}
                          >
                            <TableCell colSpan={colCount}>
                              <div className="flex items-center gap-2 font-medium">
                                {expandedGroups.has(key) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <Layers className="h-4 w-4" />
                                {group ? group.name : t('devices.noGroup')}
                                <Badge variant="secondary" className="ml-2">{groupDevices.length}</Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedGroups.has(key) && groupDevices.map(renderDeviceRow)}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : groupByUsers && devicesByUser ? (
                <Table>
                  <TableHeader>{deviceTableHeaders}</TableHeader>
                  <TableBody>
                    {devicesByUser.map(({ user, devices: userDevices }) => (
                      <>
                        <TableRow
                          key={`user-${user.id}`}
                          className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleUserExpand(user.id)}
                        >
                          <TableCell colSpan={colCount}>
                            <div className="flex items-center gap-2 font-medium">
                              {expandedUsers.has(user.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              <Users className="h-4 w-4" />
                              {user.name}
                              <Badge variant="secondary" className="ml-2">{userDevices.length}</Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedUsers.has(user.id) && userDevices.map(renderDeviceRow)}
                      </>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHeader>{deviceTableHeaders}</TableHeader>
                  <TableBody>
                    {visibleDevices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground">
                          {searchQuery ? t('devices.noDevicesMatch') : t('devices.noDevices')}
                        </TableCell>
                      </TableRow>
                    ) : paginatedDevices.map(renderDeviceRow)}
                  </TableBody>
                </Table>
              )}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    {(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, visibleDevices.length)} / {visibleDevices.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled={safeCurrentPage <= 1} onClick={() => setCurrentPage(1)}>«</Button>
                    <Button variant="outline" size="sm" disabled={safeCurrentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>‹</Button>
                    <span className="text-sm px-2">{safeCurrentPage} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={safeCurrentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</Button>
                    <Button variant="outline" size="sm" disabled={safeCurrentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>»</Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="groups" className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={openCreateGroup}>
                <Plus className="h-4 w-4 mr-1" /> {t('devices.createGroup')}
              </Button>
            </div>
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    {(['id', 'name', 'devicesCount', 'users'] as GroupSortField[]).map(field => (
                      <TableHead key={field} className="cursor-pointer hover:bg-muted/50" onClick={() => handleGroupSort(field)}>
                        <div className="flex items-center gap-1">
                          {field === 'devicesCount' ? t('devices.devicesCount') : t(`devices.${field}`)}
                          <GroupSortIcon field={field} />
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="w-24">{t('devices.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {t('devices.noGroups')}
                      </TableCell>
                    </TableRow>
                  ) : sortedGroups.map(g => (
                    <TableRow key={g.id}>
                      <TableCell className="font-mono text-sm text-muted-foreground">{g.id}</TableCell>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{g.devicesCount}</Badge>
                      </TableCell>
                      <TableCell>
                        {g.users.length > 0
                          ? <span className="text-sm">{g.users.map(u => u.name).join(', ')}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditGroup(g)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            disabled={deleteGroupMutation.isPending}
                            onClick={() => deleteGroupMutation.mutate(g.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Dialog open={groupDialogOpen} onOpenChange={(open) => { setGroupDialogOpen(open); if (!open) { setEditingGroup(null); setGroupName(''); setGroupDeviceIds(new Set()); setGroupDeviceSearch(''); } }}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleGroupSubmit}>
            <DialogHeader>
              <DialogTitle>{editingGroup ? t('devices.editGroup') : t('devices.createGroup')}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="group-name">{t('devices.name')}</Label>
                <Input
                  id="group-name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder={t('devices.name')}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label>{t('devices.devicesCount')} ({groupDeviceIds.size})</Label>
                <div className="mt-1 relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={groupDeviceSearch}
                    onChange={(e) => setGroupDeviceSearch(e.target.value)}
                    placeholder={t('common.search')}
                    className="pl-8 mb-2"
                  />
                </div>
                <div className="border rounded-md max-h-52 overflow-y-auto">
                  {devices
                    .filter(d => !groupDeviceSearch || d.name.toLowerCase().includes(groupDeviceSearch.toLowerCase()))
                    .map(d => (
                      <label key={d.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer text-sm">
                        <Checkbox
                          checked={groupDeviceIds.has(d.id)}
                          onCheckedChange={(checked) => {
                            setGroupDeviceIds(prev => {
                              const next = new Set(prev);
                              if (checked) next.add(d.id); else next.delete(d.id);
                              return next;
                            });
                          }}
                        />
                        <span className="truncate">{d.name}</span>
                      </label>
                    ))}
                  {devices.filter(d => !groupDeviceSearch || d.name.toLowerCase().includes(groupDeviceSearch.toLowerCase())).length === 0 && (
                    <p className="text-center text-muted-foreground text-sm py-4">{t('devices.noDevicesMatch')}</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSavingGroup}>
                {isSavingGroup && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingGroup ? t('common.update') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isImportOpen} onOpenChange={(open) => {
        setIsImportOpen(open);
        if (!open) { setImportRows([]); setImportRawHeaders([]); setImportRawData([]); setImportColumnMap({}); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('reseller.vehicle.import')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input type="file" accept=".csv,.tsv,.txt" onChange={handleImportFile} className="text-sm" />

            {/* Step 1: Column mapping */}
            {importRawHeaders.length > 0 && importRows.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">Match your columns to vehicle fields:</p>
                <div className="border rounded overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">CSV column</th>
                        <th className="px-3 py-2 text-left font-medium">Vehicle field</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRawHeaders.map(h => (
                        <tr key={h} className="border-t">
                          <td className="px-3 py-1 font-mono">{h}</td>
                          <td className="px-3 py-1">
                            <Select
                              value={importColumnMap[h] || '__ignore__'}
                              onValueChange={val => setImportColumnMap(prev => ({ ...prev, [h]: val === '__ignore__' ? '' : val as keyof Vehicle }))}
                            >
                              <SelectTrigger className="h-7 text-xs w-44">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__ignore__">— ignore —</SelectItem>
                                {IMPORT_COLUMNS.map(col => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-1 text-muted-foreground truncate max-w-[160px]">
                            {importRawData[0]?.[importRawHeaders.indexOf(h)] ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button onClick={() => {
                  const rows = buildImportRows(importColumnMap);
                  if (!rows.length) { toast.error(t('common.error')); return; }
                  setImportRows(rows);
                }} className="w-full">
                  Preview {importRawData.length} rows
                </Button>
              </div>
            )}

            {/* Step 2: Preview + confirm */}
            {importRows.length > 0 && (() => {
              const cols = IMPORT_COLUMNS.filter(c => importRows.some(r => r[c] != null));
              return (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{t('reseller.vehicle.importPreview', { count: importRows.length })}</p>
                    <Button variant="ghost" size="sm" onClick={() => setImportRows([])}>← Back</Button>
                  </div>
                  <div className="overflow-x-auto border rounded">
                    <table className="text-xs w-full">
                      <thead className="bg-muted">
                        <tr>{cols.map(c => <th key={c} className="px-2 py-1 text-left font-medium">{c}</th>)}</tr>
                      </thead>
                      <tbody>
                        {importRows.map((row, i) => (
                          <tr key={i} className="border-t">
                            {cols.map(c => <td key={c} className="px-2 py-1 whitespace-nowrap">{String(row[c] ?? '-')}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button onClick={handleImport} disabled={isImporting} className="w-full">
                    {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {t('reseller.vehicle.importConfirm', { count: importRows.length })}
                  </Button>
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
      {addVehicleOpen && (
        <AddVehicleDialog
          open={addVehicleOpen}
          onOpenChange={setAddVehicleOpen}
        />
      )}
      {mergeDialogOpen && selectedDevices.size === 2 && (() => {
        const [id1, id2] = Array.from(selectedDevices);
        const d1 = devices.find(d => d.id === id1);
        const d2 = devices.find(d => d.id === id2);
        if (!d1 || !d2) return null;
        return (
          <MergeDevicesDialog
            open={mergeDialogOpen}
            onOpenChange={setMergeDialogOpen}
            devices={[d1, d2]}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['device-merges'] });
              setSelectedDevices(new Set());
            }}
          />
        );
      })()}
      <Dialog open={!!assignCustomerDevice} onOpenChange={(open) => { if (!open) setAssignCustomerDevice(null); }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('devices.assignCustomer')}</DialogTitle>
            {assignCustomerDevice && (
              <p className="text-sm text-muted-foreground">{assignCustomerDevice.name}</p>
            )}
          </DialogHeader>
          <div className="py-4">
            <Label>{t('devices.customer')}</Label>
            <Command className="mt-1 border rounded-md">
              <CommandInput placeholder={t('common.search')} />
              <CommandEmpty>{t('common.noResults')}</CommandEmpty>
              <CommandList className="max-h-60">
                <CommandGroup>
                  <CommandItem value="none" onSelect={() => setAssignCustomerValue('none')}>
                    <Check className={cn('mr-2 h-4 w-4', assignCustomerValue === 'none' || !assignCustomerValue ? 'opacity-100' : 'opacity-0')} />
                    —
                  </CommandItem>
                  {customers.filter(c => c.traccar_user_id).sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                    <CommandItem key={c.id} value={c.name} onSelect={() => setAssignCustomerValue(c.id)}>
                      <Check className={cn('mr-2 h-4 w-4', assignCustomerValue === c.id ? 'opacity-100' : 'opacity-0')} />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignCustomerDevice(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleAssignCustomer} disabled={isAssigningCustomer}>
              {isAssigningCustomer && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Devices;
